import { ReactNode } from "react";
import { useAttestations } from "../lib/hooks/useAttestations";
import { useCurrentUser } from "../lib/hooks/useCurrentUser";
import Title from "../components/ui/Title";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "../components/ui/skeleton";
import { Alert } from "../components/ui/alert";
import AttestationAssuranceRowItem from "../components/attestations/AttestationAssuranceRowItem";
import AttestationCiteoRowItem from "../components/attestations/AttestationCiteoRowItem";
import AttestationSnaRowItem from "../components/attestations/AttestationSnaRowItem";
import { Building2, FileText, Inbox, Recycle, ShieldCheck } from "lucide-react";

interface AttestationSectionProps {
  children: ReactNode;
  description: string;
  icon: ReactNode;
  title: string;
}

const AttestationSection = ({ children, description, icon, title }: AttestationSectionProps) => (
  <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_14px_40px_-34px_rgba(15,23,42,0.5)]">
    <div className="flex items-center gap-3 border-b border-slate-100 bg-[#fbfaf6] px-5 py-4 sm:px-6">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#f1e8c9] text-[#755c14]">
        {icon}
      </div>
      <div>
        <h2 className="font-bold text-slate-950">{title}</h2>
        <p className="text-sm text-slate-500">{description}</p>
      </div>
    </div>
    {children}
  </section>
);

const LoadingRows = ({ colSpan }: { colSpan: number }) => (
  <>
    {[0, 1].map((item) => (
      <TableRow key={item}>
        <TableCell colSpan={colSpan} className="px-5 py-4 sm:px-6">
          <Skeleton className="h-12 w-full" />
        </TableCell>
      </TableRow>
    ))}
  </>
);

const EmptyRow = ({ colSpan }: { colSpan: number }) => (
  <TableRow>
    <TableCell colSpan={colSpan} className="py-10 text-center">
      <div className="flex flex-col items-center text-slate-500">
        <Inbox className="mb-3 h-7 w-7 text-slate-300" />
        <span className="text-sm">Aucune attestation disponible.</span>
      </div>
    </TableCell>
  </TableRow>
);

const AttestationsPage = () => {
  const { data: user } = useCurrentUser();
  const { data: attestations, isError } = useAttestations();

  return (
    <div className="mx-auto w-full max-w-7xl pb-8">
      <div className="mb-7">
        <Title>Mes attestations</Title>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 md:text-base">
          Consultez et téléchargez vos justificatifs d'assurance, d'éco-contribution et d'affiliation.
        </p>
      </div>

      {isError && (
        <Alert variant="destructive" className="mb-6">
          Impossible de charger vos attestations. Réessayez dans quelques instants.
        </Alert>
      )}

      <div className="mb-7 overflow-hidden rounded-3xl bg-[#173f35] text-white shadow-[0_18px_55px_-35px_rgba(23,63,53,0.9)]">
        <div className="relative flex items-center gap-5 px-5 py-6 sm:px-7 lg:px-8">
          <div className="absolute -right-12 -top-16 h-40 w-40 rounded-full border border-[#d7b64d]/30" />
          <div className="absolute right-6 top-5 h-20 w-20 rounded-full border border-[#d7b64d]/20" />
          <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/10">
            <FileText className="h-6 w-6 text-[#f4d56f]" />
          </div>
          <div className="relative">
            <h2 className="text-lg font-bold">Vos justificatifs officiels</h2>
            <p className="mt-1 max-w-2xl text-sm leading-6 text-emerald-50/80">
              Chaque document est généré à partir de votre abonnement et de vos paiements enregistrés.
            </p>
          </div>
        </div>
      </div>

      {!isError && (
        <div className="space-y-6">
        <AttestationSection
          title="Assurances apicoles"
          description="Vos couvertures et leurs périodes de validité"
          icon={<ShieldCheck className="h-5 w-5" />}
        >
          <Table>
            <TableHeader className="hidden bg-slate-50/70 sm:table-header-group">
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-[35%] px-6">Type</TableHead>
                <TableHead className="w-[35%]">Dates de validité</TableHead>
                <TableHead>Ruches</TableHead>
                <TableHead className="px-6 text-right">Document</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!attestations ? (
                <LoadingRows colSpan={4} />
              ) : attestations.assurances.length === 0 ? (
                <EmptyRow colSpan={4} />
              ) : (
                attestations.assurances.map((assu) => (
                  <AttestationAssuranceRowItem key={assu.id} assurance={assu} />
                ))
              )}
            </TableBody>
          </Table>
        </AttestationSection>

        <AttestationSection
          title="Éco-contributions"
          description="Vos justificatifs CITEO et LEKO"
          icon={<Recycle className="h-5 w-5" />}
        >
          <Table>
            <TableHeader className="hidden bg-slate-50/70 sm:table-header-group">
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-[35%] px-6">Organisme</TableHead>
                <TableHead className="w-[35%]">Dates de validité</TableHead>
                <TableHead>Ruches</TableHead>
                <TableHead className="px-6 text-right">Document</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!attestations ? (
                <LoadingRows colSpan={4} />
              ) : attestations.citeo.length === 0 ? (
                <EmptyRow colSpan={4} />
              ) : (
                attestations.citeo.map((abo) => (
                  <AttestationCiteoRowItem key={abo.id} abonnement={abo} user={user} />
                ))
              )}
            </TableBody>
          </Table>
        </AttestationSection>

        <AttestationSection
          title="Affiliation SNA"
          description="Vos attestations d'adhésion à une structure affiliée"
          icon={<Building2 className="h-5 w-5" />}
        >
          <Table>
            <TableHeader className="hidden bg-slate-50/70 sm:table-header-group">
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-[40%] px-6">Attestation</TableHead>
                <TableHead className="w-[40%]">Dates de validité</TableHead>
                <TableHead className="px-6 text-right">Document</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!attestations ? (
                <LoadingRows colSpan={3} />
              ) : attestations.sna.length === 0 ? (
                <EmptyRow colSpan={3} />
              ) : (
                attestations.sna.map((abo) => (
                  <AttestationSnaRowItem key={abo.id} abonnement={abo} />
                ))
              )}
            </TableBody>
          </Table>
        </AttestationSection>
        </div>
      )}
    </div>
  );
};

export default AttestationsPage;
